import {useEffect} from 'react';
import {defaultTheme} from '@stencil/build-theme';

// SELECTORS
import parseDateIntoHermesString from '@stencil/hotels-utils/src/date-utils/parse-date-into-hermes-string';
import {
  roomsDataSelector,
  hotelLocationSelector,
  isEntirePropertySelector,
  isAltAccoSelector,
  stayTypeSelector,
  hotelNameSelector,
  similarPropertiesSelector,
  staticApiRequestSelector,
  entirePropertyMergedTypeSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

import {
  checkinSelectorFn,
  checkoutSelectorFn,
  roomStringSelectorFn,
  goingForwardPropertyIdSelector,
  countrySelectorFn,
  locusSelector,
} from '@stencil/hotels-common-reducer/src/selectors';

// UTILS
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import getCgNoOfRoomFlavors from '@stencil/hotels-utils/src/get-cg-no-of-room-flavors';
import getSrpUrl from '@stencil/hotels-utils/src/get-srp-url';
import pluralise from '@stencil/hotels-utils/src/common-pluralise-if-necessary';

// CONFIG
import {
  scrollIntoViewDetails,
  scrollToEl,
} from '@stencil/hotels-utils/src/scroll-into-view-details';
import splitRoomString from '@stencil/hotels-utils/src/split-room-string';
import {gaConfig} from '@stencil/hotels-config';

// TYPES
import {isDate} from '@stencil/type-guards';
import {FetchStatusType} from '@stencil/hotels-cg-reducer/src/details/types';

// HOOKS
import {useSelector, useDispatch} from 'react-redux';
import DPSoldOut from '../../../../../stories/components/hotels/DetailsPage/SoldOut/DPSoldOut';
import useCurrentRoomData from '../../../../hooks/useCurrentRoomData';

// COMPONENTS
import Text from '../../../../../stories/core-components/TextFieldExpt/TextFieldExpt';
import RoomPerNightUI from '../../../../../stories/components/hotels/DetailsPageUnif/RoomPerNightUI/RoomPerNightUI';
import PersuasionHoverText from '../../../../../stories/components/hotels/DetailsPageUnif/PersuasionHoverText/PersuasionHoverText';
import CheckAvailabilityAltAcco from '../../../../../stories/components/hotels/DetailsPageUnif/CheckAvailabilityAltAcco/CheckAvailabilityAltAcco';

// ICONS
import RupeeIcon from '../../../../../stories/Icons/RupeeIcon';
import UserIcon from '../../../../../stories/Icons/UserIcon';
import DoubleBedIcon from '../../../../../stories/Icons/DoubleBedIcon';
import RightDoubleArrowIcon from '../../../../../stories/Icons/RightDoubleArrow';

// STYLES
import {
  FlexCentered,
  HiddenElement,
} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import * as S from './styles/BookingWidget.styles';

import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';

const {DetailsGAClickEventActions} = gaConfig;

export default function BookingWidget({
  showPersuasion = false,
  hotelLevelPersuasion = {},
  handlOpenCalendar = () => {},
  altAcco = false,
  isDatePickRequired = false,
}) {
  const roomsDataFromCg = useSelector(roomsDataSelector);
  const checkin = useSelector(checkinSelectorFn);
  const checkout = useSelector(checkoutSelectorFn);
  const hotelId = useSelector(goingForwardPropertyIdSelector);
  const location = useSelector(hotelLocationSelector);
  const hotelName = useSelector(hotelNameSelector);
  const country = useSelector(countrySelectorFn);
  const locus = useSelector(locusSelector);
  const roomString = useSelector(roomStringSelectorFn);
  const isAltAcco = useSelector(isAltAccoSelector);
  const stayType = useSelector(stayTypeSelector);
  const mergedPropType = useSelector(entirePropertyMergedTypeSelector);
  const similarPropertiesData = useSelector(similarPropertiesSelector);
  const staticApiRequest = useSelector(staticApiRequestSelector);
  const isEntireProperty = useSelector(isEntirePropertySelector);

  const isSimilarHotelsFetchCompleted = staticApiRequest?.success || !!staticApiRequest?.error;

  const thereAreSimilarHotels = similarPropertiesData?.slice(1)?.length;

  const dispatch = useDispatch();

  const {roomCount, paxCount, childCount} = splitRoomString(roomString);

  const totalNoOfRooms =
    (roomsDataFromCg && roomsDataFromCg.length && getCgNoOfRoomFlavors(roomsDataFromCg)) || 0;

  const {roomPrice, tax, roomInfo = {}, roomCount: rateplanRoomCount} = useCurrentRoomData();

  const {roomName} = roomInfo;

  const noOfRooms = roomCount;
  const noOfGuests = paxCount + childCount;
  const isSoldOut = !roomsDataFromCg.length;

  useEffect(() => {
    if (isSoldOut) {
      const checkinDate = parseDateIntoHermesString(checkin.date);
      const checkoutDate = parseDateIntoHermesString(checkout.date);

      const eventLabel = hotelId + '_' + roomString + '_' + checkinDate + '_' + checkoutDate;
      dispatch(
        detailsPagePushGaThunk({
          eventAction: DetailsGAClickEventActions.soldout.soldOut,
          eventLabel,
        }),
      );
    }
  }, [isSoldOut, hotelId, checkin, checkout, roomString]);

  let srpUrl = '';

  const destination = {
    _id: location?.id,
    n: location?.name,
  };

  if (
    checkin &&
    isDate(checkin.date) &&
    checkout &&
    isDate(checkout.date) &&
    roomString &&
    ((destination?._id && destination?.n) || locus?.id)
  ) {
    srpUrl = getSrpUrl({
      checkin,
      checkout,
      roomString,
      destination: destination,
      country,
    });
  }

  const nonSoldOutUI = (
    <S.BookingWidgetStyleContainer background={!!altAcco && isDatePickRequired}>
      {!!roomName && (
        <S.PriceStartsAtText>
          {noOfRooms > 1 && !!mergedPropType ? `${noOfRooms} x ${roomName}` : roomName}
        </S.PriceStartsAtText>
      )}

      <S.PriceInfo isEntireProperty={isEntireProperty}>
        <S.RoomAndPersuasionContainer>
          <S.Column isRow isEntireProperty={isEntireProperty}>
            {!!noOfGuests && (
              <S.NoOfGuestsContainer isEntireProperty={isEntireProperty}>
                <UserIcon
                  width="1.4rem"
                  height="1.4rem"
                  fill={defaultTheme.colors.downriver.medium}
                />
                <S.NoOfGuestsAndRoomsTextStyles>
                  {`${noOfGuests} Guest${noOfGuests > 1 ? 's' : ''}`}
                  {/* {isAltAcco
                  ? `${noOfGuests} Guest${noOfGuests > 1 ? 's' : ''}`
                  : `${noOfGuests} x Guest${noOfGuests > 1 ? 's' : ''}`} */}
                </S.NoOfGuestsAndRoomsTextStyles>
              </S.NoOfGuestsContainer>
            )}
            {!!noOfGuests && !!noOfRooms && !isEntireProperty ? (
              <div style={{margin: '0 5px'}}> | </div>
            ) : (
              ''
            )}

            {!!noOfRooms && (
              <S.NoOfRoomsContainer>
                <DoubleBedIcon
                  width="1.4rem"
                  height="1.4rem"
                  fill={defaultTheme.colors.downriver.medium}
                />
                <S.NoOfGuestsAndRoomsTextStyles isEntireProperty={isEntireProperty}>
                  {!!mergedPropType
                    ? `${noOfRooms} ${mergedPropType}`
                    : isAltAcco
                      ? `${noOfRooms}  ${roomInfo?.ratePlans?.[0]?.sellableType}${
                          noOfRooms > 1 ? 's' : ''
                        }`
                      : `${noOfRooms} Room${noOfRooms > 1 ? 's' : ''}`}
                </S.NoOfGuestsAndRoomsTextStyles>
              </S.NoOfRoomsContainer>
            )}
          </S.Column>
          {showPersuasion ? (
            <S.PersuasionContainer>
              <PersuasionHoverText
                data={hotelLevelPersuasion?.bestOffersSection}
                persuasionTitleStyles={{padding: '0'}}
              />
            </S.PersuasionContainer>
          ) : null}
        </S.RoomAndPersuasionContainer>
        <S.Column isAlignEnd>
          {!!roomPrice && (
            <FlexCentered>
              <RupeeIcon width="1.4rem" height="1.4rem" fill={defaultTheme.colors.downriver.base} />
              <S.PriceValue price={roomPrice} />
            </FlexCentered>
          )}

          {!!tax && (
            <FlexCentered>
              +
              <S.PlusPriceValue price={tax} />
            </FlexCentered>
          )}
          {!!roomPrice && (
            <S.PerRoomPerNightLabel isEntireProperty={isEntireProperty}>
              <RoomPerNightUI
                roomCount={isEntireProperty ? '' : rateplanRoomCount}
                showRoomText={!mergedPropType}
                roomText={pluralise(roomInfo?.ratePlans?.[0]?.sellableType, rateplanRoomCount)}
              />
            </S.PerRoomPerNightLabel>
          )}
        </S.Column>
      </S.PriceInfo>

      {!!roomPrice && (
        <HiddenElement itemProp="priceRange">
          {`${hotelName} Price Starts at INR ${roomPrice}/Night`}
        </HiddenElement>
      )}
      {altAcco && isDatePickRequired ? (
        <CheckAvailabilityAltAcco handlOpenCalendar={handlOpenCalendar} needBorderLine />
      ) : !!totalNoOfRooms ? (
        <S.ViewRoomOptionsButton
          onClick={e => {
            dispatch(
              omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                cta: {
                  name: `${DetailsPageComponents.BookingWidget}_${detailsClick.detailsQuickBook.quickbook_HD_click}`,
                  type: omnitureConfig.ctaType.Button,
                  componentName: DetailsPageComponents.BookingWidget,
                  destination: '',
                },
              }),
            );
            scrollIntoViewDetails(
              document.querySelector(
                isAltAcco
                  ? detailsNavigationIdsAndValues.stays.id
                  : detailsNavigationIdsAndValues.rooms.id,
              ),
            );
          }}
        >
          <Text bold customFontSize="1.6">
            <span>
              VIEW {totalNoOfRooms} {isEntireProperty ? `PROPERTY` : `ROOM`} OPTION
              {totalNoOfRooms > 1 ? 'S' : ''}
            </span>
            <S.RightDoubleArrowIconContainer>
              <RightDoubleArrowIcon
                width="2rem"
                height="2rem"
                fill={defaultTheme.colors.goWhite.base}
              />
            </S.RightDoubleArrowIconContainer>
          </Text>
        </S.ViewRoomOptionsButton>
      ) : null}
    </S.BookingWidgetStyleContainer>
  );

  return (
    <>
      {isSoldOut ? (
        <DPSoldOut
          tryAlternateDatesClick={() => {
            dispatch(
              omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                cta: {
                  name: `${DetailsPageComponents.BookingWidget}_${detailsClick.soldout.showAlternateDatesClick}`,
                  type: omnitureConfig.ctaType.Button,
                  componentName: DetailsPageComponents.BookingWidget,
                  destination: '',
                },
              }),
            );

            const calendarEl = document.getElementById('search-widget-checkin-input');

            if (calendarEl) {
              calendarEl.click();
            }
          }}
          showSimilarHotelsClick={() => {
            dispatch(
              omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                cta: {
                  name: `${DetailsPageComponents.BookingWidget}_${detailsClick.soldout.showSimilarHotelsClick}`,
                  type: omnitureConfig.ctaType.Hyperlink,
                  componentName: DetailsPageComponents.BookingWidget,
                  destination: '',
                },
              }),
            );

            scrollToEl(detailsNavigationIdsAndValues.similarHotels.id);
          }}
          showSimilarHotelsText={thereAreSimilarHotels}
          onSeeOtherHotelsClick={() => {
            dispatch(
              omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
                cta: {
                  name: `${DetailsPageComponents.BookingWidget}_${detailsClick.soldout.seeOtherHotelsClick}`,
                  type: omnitureConfig.ctaType.Hyperlink,
                  componentName: DetailsPageComponents.BookingWidget,
                  destination: '',
                },
              }),
            );
          }}
          srpUrl={thereAreSimilarHotels || !isSimilarHotelsFetchCompleted ? '' : srpUrl}
          cityName={location?.name}
        />
      ) : (
        nonSoldOutUI
      )}
    </>
  );
}
